import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { myTimeReportAPI, monthlyTimesheetAPI } from '../services/api';
import SearchableSelect from './SearchableSelect';
import ExpandableNotes from './ExpandableNotes';
import type {
  DateRangeType,
  MyTimeReportParams,
  MyTimeReportPreview,
  MyTimeReportProject,
  MonthlyTimesheetDetail,
} from '../types';

const formatDate = (dateString: string) => {
  const [year, month, day] = dateString.split('-').map((n) => parseInt(n, 10));
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatHours = (hours: string | number) => parseFloat(hours.toString()).toFixed(2);

const getMonthBounds = (year: number, month: number) => {
  const start = new Date(year, month - 1, 1);
  const end = new Date(year, month, 0);
  const toIso = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  return { start: toIso(start), end: toIso(end) };
};

const MyTimesheets: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [projects, setProjects] = useState<MyTimeReportProject[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [dateRangeType, setDateRangeType] = useState<DateRangeType>('current_month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [preview, setPreview] = useState<MyTimeReportPreview | null>(null);
  const [monthlyTimesheet, setMonthlyTimesheet] = useState<MonthlyTimesheetDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initializedFromQuery, setInitializedFromQuery] = useState(false);

  // Admin on-behalf: ?user_id= from Time Entry Review & Submit
  const viewUserId = useMemo(() => {
    const raw = searchParams.get('user_id');
    if (!raw) return undefined;
    const id = parseInt(raw, 10);
    return Number.isNaN(id) ? undefined : id;
  }, [searchParams]);

  // Initialize from ?year=&month= query params once
  useEffect(() => {
    if (initializedFromQuery) return;
    const yearParam = searchParams.get('year');
    const monthParam = searchParams.get('month');
    if (yearParam && monthParam) {
      const year = parseInt(yearParam, 10);
      const month = parseInt(monthParam, 10);
      if (!Number.isNaN(year) && !Number.isNaN(month) && month >= 1 && month <= 12) {
        const today = new Date();
        const isCurrent =
          year === today.getFullYear() && month === today.getMonth() + 1;
        const prev = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const isPrevious =
          year === prev.getFullYear() && month === prev.getMonth() + 1;

        if (isCurrent) {
          setDateRangeType('current_month');
        } else if (isPrevious) {
          setDateRangeType('previous_month');
        } else {
          setDateRangeType('custom');
          const bounds = getMonthBounds(year, month);
          setStartDate(bounds.start);
          setEndDate(bounds.end);
        }
      }
    }
    setInitializedFromQuery(true);
  }, [searchParams, initializedFromQuery]);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await myTimeReportAPI.projects(
          viewUserId != null ? { user_id: viewUserId } : undefined
        );
        setProjects(response.data);
      } catch {
        setError('Failed to load projects');
      }
    };
    fetchProjects();
  }, [viewUserId]);

  useEffect(() => {
    if (!initializedFromQuery) return;
    const today = new Date();
    if (dateRangeType === 'all_time') {
      setStartDate('');
      setEndDate('');
    } else if (dateRangeType === 'current_month') {
      const bounds = getMonthBounds(today.getFullYear(), today.getMonth() + 1);
      setStartDate(bounds.start);
      setEndDate(bounds.end);
    } else if (dateRangeType === 'previous_month') {
      const prev = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const bounds = getMonthBounds(prev.getFullYear(), prev.getMonth() + 1);
      setStartDate(bounds.start);
      setEndDate(bounds.end);
    }
  }, [dateRangeType, initializedFromQuery]);

  const calendarMonth = useMemo(() => {
    if (!startDate || !endDate) return null;
    const [sy, sm] = startDate.split('-').map(Number);
    const [ey, em] = endDate.split('-').map(Number);
    if (sy !== ey || sm !== em) return null;
    const bounds = getMonthBounds(sy, sm);
    if (startDate === bounds.start && endDate === bounds.end) {
      return { year: sy, month: sm };
    }
    return null;
  }, [startDate, endDate]);

  const buildParams = useCallback((): MyTimeReportParams => {
    const params: MyTimeReportParams = { date_range_type: dateRangeType };
    if (selectedProjectId != null) params.project_id = selectedProjectId;
    if (viewUserId != null) params.user_id = viewUserId;
    if (dateRangeType === 'custom') {
      params.start_date = startDate;
      params.end_date = endDate;
    }
    return params;
  }, [dateRangeType, selectedProjectId, startDate, endDate, viewUserId]);

  const loadPreview = useCallback(async () => {
    if (dateRangeType === 'custom' && (!startDate || !endDate)) {
      setError('Please select start and end dates');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const response = await myTimeReportAPI.preview(buildParams());
      setPreview(response.data);
    } catch (err: unknown) {
      console.error('Failed to load my timesheets', err);
      setError('Failed to load time entries');
      setPreview(null);
    } finally {
      setLoading(false);
    }
  }, [buildParams, dateRangeType, startDate, endDate]);

  const loadMonthlyTimesheet = useCallback(async () => {
    if (!calendarMonth) {
      setMonthlyTimesheet(null);
      return;
    }
    try {
      const response = await monthlyTimesheetAPI.getForMonth(
        calendarMonth.year,
        calendarMonth.month,
        viewUserId
      );
      setMonthlyTimesheet(response.data);
    } catch {
      setMonthlyTimesheet(null);
    }
  }, [calendarMonth, viewUserId]);

  useEffect(() => {
    if (!initializedFromQuery) return;
    if (dateRangeType === 'custom' && (!startDate || !endDate)) return;
    loadPreview();
    loadMonthlyTimesheet();
  }, [
    initializedFromQuery,
    dateRangeType,
    startDate,
    endDate,
    selectedProjectId,
    viewUserId,
    loadPreview,
    loadMonthlyTimesheet,
  ]);

  const updateQueryForMonth = (year: number, month: number) => {
    const next: Record<string, string> = {
      year: String(year),
      month: String(month),
    };
    if (viewUserId != null) next.user_id = String(viewUserId);
    setSearchParams(next);
  };

  const handleSubmitTimesheet = async () => {
    if (!monthlyTimesheet?.id || !calendarMonth) return;
    const monthLabel = new Date(
      calendarMonth.year,
      calendarMonth.month - 1
    ).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    if (
      !window.confirm(
        `Submit timesheet for ${monthLabel} for approval?\n\nOnce submitted, you will not be able to edit time entries for this month until it is approved or rejected.`
      )
    ) {
      return;
    }
    try {
      setSubmitting(true);
      await monthlyTimesheetAPI.submit(monthlyTimesheet.id);
      alert('Timesheet submitted successfully!');
      await loadMonthlyTimesheet();
      await loadPreview();
      updateQueryForMonth(calendarMonth.year, calendarMonth.month);
    } catch (err) {
      console.error('Failed to submit timesheet', err);
      alert('Failed to submit timesheet. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return '#7f8c8d';
      case 'submitted': return '#f39c12';
      case 'approved': return '#27ae60';
      case 'invoiced': return '#2980b9';
      default: return '#7f8c8d';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'draft': return 'Draft';
      case 'submitted': return 'Submitted - Pending Approval';
      case 'approved': return 'Approved';
      case 'invoiced': return 'Invoiced';
      default: return status;
    }
  };

  const projectOptions = [
    { value: null as number | null, label: 'All projects' },
    ...projects.map((p) => ({
      value: p.id,
      label: `${p.code} - ${p.name}`,
    })),
  ];

  return (
    <div className="container">
      <h2>My Timesheets</h2>
      <p style={{ color: '#6c757d', marginBottom: '1.5rem' }}>
        Review {preview?.user?.name ? `${preview.user.name}'s` : 'your'} time for the
        current or previous month, or a custom range. Filter by project, confirm
        entries, then submit for approval.
      </p>
      {viewUserId != null && preview?.user && (
        <div
          style={{
            padding: '0.75rem 1rem',
            marginBottom: '1rem',
            backgroundColor: '#e8f4f8',
            borderRadius: '4px',
            color: '#0c5460',
          }}
        >
          Viewing timesheet for <strong>{preview.user.name}</strong> (on behalf of user)
        </div>
      )}

      {error && (
        <div
          style={{
            padding: '0.75rem 1rem',
            marginBottom: '1rem',
            backgroundColor: '#f8d7da',
            color: '#721c24',
            borderRadius: '4px',
          }}
        >
          {error}
        </div>
      )}

      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '1rem',
            alignItems: 'end',
          }}
        >
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label htmlFor="my-date-range">Date Range</label>
            <select
              id="my-date-range"
              value={dateRangeType}
              onChange={(e) => {
                setDateRangeType(e.target.value as DateRangeType);
                setPreview(null);
              }}
            >
              <option value="current_month">Current Month</option>
              <option value="previous_month">Previous Month</option>
              <option value="custom">Custom Range</option>
              <option value="all_time">All Time</option>
            </select>
          </div>

          {dateRangeType === 'custom' && (
            <>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="my-start-date">Start Date</label>
                <input
                  type="date"
                  id="my-start-date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="my-end-date">End Date</label>
                <input
                  type="date"
                  id="my-end-date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </>
          )}

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label htmlFor="my-project-filter">Project</label>
            <SearchableSelect
              id="my-project-filter"
              options={projectOptions}
              value={selectedProjectId}
              onChange={(v) => setSelectedProjectId(v === null || v === '' ? null : Number(v))}
              placeholder="Search projects..."
              aria-label="Project"
            />
          </div>
        </div>
      </div>

      {monthlyTimesheet && calendarMonth && (
        <div
          style={{
            padding: '1rem',
            marginBottom: '1.5rem',
            backgroundColor: monthlyTimesheet.can_edit ? '#e8f5e9' : '#fff3cd',
            border: `2px solid ${getStatusColor(monthlyTimesheet.status)}`,
            borderRadius: '8px',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '1rem',
            }}
          >
            <div>
              <h3
                style={{
                  margin: '0 0 0.5rem 0',
                  color: getStatusColor(monthlyTimesheet.status),
                }}
              >
                {new Date(
                  calendarMonth.year,
                  calendarMonth.month - 1
                ).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}{' '}
                Timesheet
              </h3>
              <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
                <div>
                  <strong>Status:</strong>{' '}
                  <span
                    style={{
                      color: getStatusColor(monthlyTimesheet.status),
                      fontWeight: 'bold',
                    }}
                  >
                    {getStatusLabel(monthlyTimesheet.status)}
                  </span>
                </div>
                <div>
                  <strong>Total Hours:</strong>{' '}
                  {parseFloat(monthlyTimesheet.total_hours || '0').toFixed(2)}
                </div>
                <div>
                  <strong>Entries:</strong> {monthlyTimesheet.entries_count || 0}
                </div>
              </div>
              {monthlyTimesheet.rejection_reason &&
                monthlyTimesheet.status === 'draft' && (
                  <div
                    style={{
                      marginTop: '0.5rem',
                      padding: '0.5rem',
                      backgroundColor: '#fee',
                      borderRadius: '4px',
                      color: '#c0392b',
                    }}
                  >
                    <strong>Rejected:</strong> {monthlyTimesheet.rejection_reason}
                  </div>
                )}
            </div>
            {monthlyTimesheet.can_submit && monthlyTimesheet.can_edit && (
              <button
                type="button"
                onClick={handleSubmitTimesheet}
                className="btn btn-primary"
                disabled={submitting}
                style={{ whiteSpace: 'nowrap' }}
              >
                {submitting ? 'Submitting...' : 'Submit for Approval'}
              </button>
            )}
          </div>
        </div>
      )}

      {loading && <p>Loading time entries...</p>}

      {!loading && preview && (
        <>
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '1rem',
              }}
            >
              <div>
                <strong>Period:</strong>{' '}
                {preview.start_date && preview.end_date
                  ? `${formatDate(preview.start_date)} – ${formatDate(preview.end_date)}`
                  : 'All Time'}
              </div>
              <div>
                <strong>Total Hours:</strong> {formatHours(preview.grand_total_hours)}
              </div>
              <div>
                <strong>Entries:</strong> {preview.entries.length}
              </div>
            </div>
          </div>

          {preview.summary_by_project.length > 0 && (
            <div className="card" style={{ marginBottom: '1.5rem' }}>
              <h3>Summary by Project</h3>
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Project</th>
                      <th style={{ textAlign: 'right' }}>Hours</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.summary_by_project.map((s) => (
                      <tr key={s.project_id}>
                        <td>
                          {s.project_code} - {s.project_name}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {formatHours(s.total_hours)}
                        </td>
                      </tr>
                    ))}
                    <tr style={{ fontWeight: 'bold', borderTop: '2px solid #dee2e6' }}>
                      <td>Total</td>
                      <td style={{ textAlign: 'right' }}>
                        {formatHours(preview.grand_total_hours)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="card">
            <h3>Time Entries ({preview.entries.length})</h3>
            {preview.entries.length === 0 ? (
              <p style={{ color: '#6c757d' }}>No time entries for this selection.</p>
            ) : (
              <div className="table-container">
                <table style={{ fontSize: '0.9rem' }}>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Project</th>
                      <th>Rate</th>
                      <th style={{ textAlign: 'right' }}>Hours</th>
                      <th style={{ textAlign: 'center' }}>Billable</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.entries.map((entry) => (
                      <tr key={entry.id}>
                        <td style={{ whiteSpace: 'nowrap' }}>{formatDate(entry.date)}</td>
                        <td>
                          {entry.project_code} - {entry.project_name}
                        </td>
                        <td>{entry.rate_name}</td>
                        <td style={{ textAlign: 'right' }}>{formatHours(entry.hours)}</td>
                        <td style={{ textAlign: 'center' }}>
                          <span
                            style={{
                              display: 'inline-block',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '0.75rem',
                              backgroundColor: entry.is_billable ? '#d4edda' : '#e2e3e5',
                              color: entry.is_billable ? '#155724' : '#383d41',
                            }}
                          >
                            {entry.is_billable ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td style={{ maxWidth: '280px' }}>
                          <ExpandableNotes notes={entry.notes} emptyDisplay="—" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default MyTimesheets;
